import { Controller } from '@hotwired/stimulus'
import * as bootstrap from 'bootstrap'

/**
 * OnlineDealerTabsController
 *
 * Opens the tab a link is pointing into, on the online dealer edit form.
 *
 * The form's sections are panes now, and all but one of them is closed at any moment.
 * That is fine for somebody working top to bottom and wrong for everybody arriving from
 * somewhere else - which on this page is the common case. The visibility panel above
 * the form lists what a listing is missing and links each fact to the field that fixes
 * it, and every one of those anchors now names an element inside a closed pane, where a
 * browser's own jump does nothing at all.
 *
 * So the click is caught at the document, because the links are not inside the form,
 * and the pane is opened before the page is scrolled anywhere. The order matters: the
 * browser measures the scroll for the layout it can see, and a pane that is still
 * fading in has not taken its height yet, so its own jump lands short.
 *
 * A link naming something outside these panes - the help modal's trigger, an anchor on
 * the page proper - is left entirely alone.
 *
 * Expected markup:
 *   <div data-controller="online-dealer-tabs"
 *        data-action="click@document->online-dealer-tabs#follow">
 *     <button data-bs-toggle="tab" data-bs-target="#a-pane">…</button>
 *     <div class="tab-pane" id="a-pane"><input id="a-field"></div>
 *   </div>
 */
export default class extends Controller {
  connect() {
    // A url that arrived carrying a fragment: somebody's bookmark, or the reload after
    // following one of the panel's links. Nothing has been clicked, so #follow will
    // never run and the pane would stay shut on the field being asked for.
    const id = window.location.hash.slice(1)
    if (id) this.#open(this.#targetById(id))
  }

  follow(event) {
    // Modified clicks are the reader asking for a new tab or window, and a bare
    // in-page anchor is not something to intercept on their behalf.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const link = event.target.closest('a[href^="#"]')
    // A link that is already driving a Bootstrap widget - a collapse toggle, another
    // tab - owns its own click and must not be given a second meaning here.
    if (!link || link.dataset.bsToggle) return

    const target = this.#targetById(link.getAttribute('href').slice(1))
    if (!target) return

    event.preventDefault()

    // The fragment goes on the url, which is the half connect() was already written for:
    // it opens the pane a fragment names on load, for "somebody's bookmark, or the reload
    // after following one of the panel's links" - and following one never wrote it, so
    // that reload had nothing to read and the pane came back shut.
    //
    // replaceState rather than assigning location.hash, which is what preventDefault is
    // avoiding one line up: setting the hash makes the browser jump to the element before
    // the pane holding it is shown, and #open puts the reader there itself. replace
    // rather than push, so walking back through a form's tabs is not a history of them.
    window.history.replaceState(null, '', link.getAttribute('href'))

    this.#open(target)
  }

  /**
   * Shows the pane holding an element, then puts the reader on it.
   *
   * @param {Element|null} target
   */
  #open(target) {
    if (!target) return

    const pane = target.closest('.tab-pane')
    if (!pane) return

    if (pane.classList.contains('active')) {
      this.#settle(target)
      return
    }

    const trigger = this.element.querySelector(`[data-bs-target="#${pane.id}"]`)
    const Tab = this.#tabLibrary()
    if (!trigger || !Tab) return

    // Fade first, land second - see the note at the top about measuring a pane that is
    // still opening. Bootstrap fires this on the trigger, not on the pane.
    trigger.addEventListener('shown.bs.tab', () => this.#settle(target), { once: true })
    Tab.getOrCreateInstance(trigger).show()
  }

  /**
   * A field is focused; anything else is only scrolled to. Focusing is what the link
   * promised - "Logo" on the panel above means put me in the logo field, not near it -
   * but forcing focus onto a paragraph would move a screen reader somewhere it was
   * never told about.
   *
   * @param {Element} target
   */
  #settle(target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })

    if (target.matches('input, select, textarea')) {
      target.focus({ preventScroll: true })
    }
  }

  /**
   * Only elements this controller is responsible for. A link naming something outside
   * the tabs is somebody else's, and resolving it here would be how a modal trigger or
   * a skip link quietly stopped working.
   *
   * @param {string} id
   * @return {Element|null}
   */
  #targetById(id) {
    if (!id) return null

    // A bookmark or a hand-edited URL can carry a fragment that is not valid
    // percent-encoding - "#field%" is enough - and decodeURIComponent throws rather
    // than returning anything for that. Thrown from connect(), uncaught, that failure
    // is Stimulus's to catch, and it never runs #follow again for the rest of the page
    // load: every link on the panel goes back to doing nothing, which is the one
    // failure mode this controller exists to prevent. An id that will not decode names
    // nothing this controller can resolve either way.
    let decoded
    try {
      decoded = decodeURIComponent(id)
    } catch {
      return null
    }

    const target = document.getElementById(decoded)
    return target && this.element.contains(target) ? target : null
  }

  // Resolved rather than imported outright, matching bootstrap_modal_controller: the
  // module import does not survive every bundler configuration this app is built with.
  #tabLibrary() {
    return bootstrap.Tab || window.bootstrap?.Tab
  }
}
